/**
 * Nested service hierarchy for the Presales Designer.
 *
 * Root parents (Port, SD-WAN Hub) hold children directly. Below them a
 * customer-premises chain can hang:
 *
 *   Port -> Last Mile -> CPE -> CPE Port -> Internet / Cloud -> IP Block
 *
 * A CPE is the only node with more than one parent: one CPE can be reached
 * through several Last Miles, each landing on a different Port. Its Ports are
 * created by presales (Port 1, Port 2...) and are not Odoo lines, so they get
 * a synthetic id (cpePortId). Nested links live in the same `assignments`
 * array, tagged with a nested `parentType`, so the backend
 * (services/presales/design-hierarchy.js) can tell them apart from root
 * assignments.
 */
import { NestedParentType, OdooOrderLine, ProductAssignment, ProductCategory } from './types';

// Which categories a nested parent accepts as children. A CPE holds Ports,
// not lines (see cpePortId)
export const NESTED_CHILD_CATEGORIES: Partial<Record<ProductCategory, ProductCategory[]>> = {
  last_mile: ['cpe'],
  enterprise_internet: ['ip_block'],
};

// Services a CPE Port can deliver
export const CPE_PORT_CHILD_CATEGORIES: ProductCategory[] = ['enterprise_internet', 'cloud_connectivity'];

const NESTED_PARENT_TYPE_BY_CATEGORY: Partial<Record<ProductCategory, NestedParentType>> = {
  last_mile: 'last_mile',
  enterprise_internet: 'internet',
};

/**
 * Synthetic id of a CPE Port: BASE + cpeLineId * 100 + portNumber. It never
 * collides with an Odoo line id (positive, far below BASE) nor with an
 * existing service in a DCI endpoint (negative). Mirrors cpePortId() in the
 * backend's services/presales/cpe-port.js.
 */
const CPE_PORT_ID_BASE = 1_000_000_000;
const PORTS_PER_CPE_SLOT = 100;
export const MAX_PORTS_PER_CPE = PORTS_PER_CPE_SLOT - 1;

export const cpePortId = (cpeLineId: number, portNumber: number): number =>
  CPE_PORT_ID_BASE + cpeLineId * PORTS_PER_CPE_SLOT + portNumber;

export function decodeCpePortId(id: number): { cpeLineId: number; portNumber: number } | null {
  if (!Number.isInteger(id) || id <= CPE_PORT_ID_BASE) return null;
  const offset = id - CPE_PORT_ID_BASE;
  const portNumber = offset % PORTS_PER_CPE_SLOT;
  const cpeLineId = Math.floor(offset / PORTS_PER_CPE_SLOT);
  return portNumber >= 1 && cpeLineId >= 1 ? { cpeLineId, portNumber } : null;
}

// A CPE always has at least one Port
export const DEFAULT_CPE_PORT_NUMBERS = [1];

/**
 * The design's assignments with each CPE's Ports linked under it. The Ports
 * themselves live in their own state (port numbers per CPE); this keeps the
 * tree, the reachability and the payload on a single array.
 */
export function withCpePorts(
  assignments: ProductAssignment[],
  cpeLineIds: number[],
  portNumbersByCpe: Record<number, number[]>
): ProductAssignment[] {
  const portLinks: ProductAssignment[] = cpeLineIds.map(cpeLineId => ({
    parentLineId: cpeLineId,
    childLineIds: (portNumbersByCpe[cpeLineId] ?? DEFAULT_CPE_PORT_NUMBERS).map(n => cpePortId(cpeLineId, n)),
    parentType: 'cpe',
  }));
  return [...assignments.filter(a => a.parentType !== 'cpe'), ...portLinks];
}

/**
 * Port numbers per CPE, read back from a saved design.
 */
export function cpePortNumbersFrom(assignments: ProductAssignment[]): Record<number, number[]> {
  const numbers: Record<number, number[]> = {};
  for (const assignment of assignments) {
    if (assignment.parentType !== 'cpe') continue;
    const ports = assignment.childLineIds
      .map(decodeCpePortId)
      .filter((port): port is { cpeLineId: number; portNumber: number } => port?.cpeLineId === assignment.parentLineId)
      .map(port => port.portNumber);
    if (ports.length > 0) numbers[assignment.parentLineId] = [...new Set(ports)].sort((a, b) => a - b);
  }
  return numbers;
}

/**
 * Services that can still be hung from a CPE Port: Internet or Cloud not
 * placed anywhere yet.
 */
export function cpePortCandidates(
  portId: number,
  lines: OdooOrderLine[],
  assignments: ProductAssignment[],
  reached: Set<number>
): OdooOrderLine[] {
  const current = childIdsOf(assignments, portId);
  return lines.filter(
    line =>
      CPE_PORT_CHILD_CATEGORIES.includes(line.product.category) &&
      !current.includes(line.id) &&
      !reached.has(line.id)
  );
}

// Categories that may hang from several nested parents at once
const MULTI_PARENT_CATEGORIES: ProductCategory[] = ['cpe'];

export const nestedParentTypeFor = (category: ProductCategory): NestedParentType | null =>
  NESTED_PARENT_TYPE_BY_CATEGORY[category] ?? null;

export const acceptsNestedChild = (parentCategory: ProductCategory, childCategory: ProductCategory): boolean =>
  NESTED_CHILD_CATEGORIES[parentCategory]?.includes(childCategory) ?? false;

export const allowsMultipleParents = (category: ProductCategory): boolean =>
  MULTI_PARENT_CATEGORIES.includes(category);

export const isNestedAssignment = (assignment: ProductAssignment): boolean =>
  assignment.parentType === 'last_mile' ||
  assignment.parentType === 'cpe' ||
  assignment.parentType === 'cpe_port' ||
  assignment.parentType === 'internet';

export const childIdsOf = (assignments: ProductAssignment[], parentId: number): number[] =>
  assignments.find(a => a.parentLineId === parentId)?.childLineIds ?? [];

export const parentIdsOf = (assignments: ProductAssignment[], childId: number): number[] =>
  assignments.filter(a => a.childLineIds.includes(childId)).map(a => a.parentLineId);

/**
 * Link a child to a parent. The child leaves every other parent, except
 * parents of the same nested type when the category allows several (a CPE
 * reached through two Last Miles).
 */
export function linkChild(
  assignments: ProductAssignment[],
  childId: number,
  parentId: number,
  parentType: ProductAssignment['parentType'],
  keepSameTypeParents = false
): ProductAssignment[] {
  const cleaned = assignments
    .map(a => {
      const keep = keepSameTypeParents && a.parentType === parentType;
      return keep ? a : { ...a, childLineIds: a.childLineIds.filter(id => id !== childId) };
    })
    .filter(a => a.childLineIds.length > 0);

  const index = cleaned.findIndex(a => a.parentLineId === parentId);
  if (index < 0) {
    return [...cleaned, { parentLineId: parentId, childLineIds: [childId], parentType }];
  }
  if (cleaned[index].childLineIds.includes(childId)) return cleaned;

  const updated = [...cleaned];
  updated[index] = { ...updated[index], childLineIds: [...updated[index].childLineIds, childId] };
  return updated;
}

/**
 * Unlink a child from one parent, or from all of them when no parent is given.
 */
export function unlinkChild(
  assignments: ProductAssignment[],
  childId: number,
  parentId?: number
): ProductAssignment[] {
  return assignments
    .map(a =>
      parentId === undefined || a.parentLineId === parentId
        ? { ...a, childLineIds: a.childLineIds.filter(id => id !== childId) }
        : a
    )
    .filter(a => a.childLineIds.length > 0);
}

/**
 * Line ids reachable from the roots. A nested child only counts as assigned
 * when its chain ends on a root: a CPE under a Last Mile that is itself
 * unassigned is still unassigned.
 *
 * @param rootIds - Lines that hold children without needing a parent
 *   (Ports, SD-WAN Hubs, FWaaS)
 * @param preassignedIds - Lines already placed elsewhere (existing services
 *   from previous orders); they count as reached and expose their subtree
 */
export function reachableLineIds(
  assignments: ProductAssignment[],
  rootIds: Iterable<number>,
  preassignedIds: Iterable<number> = []
): Set<number> {
  const reached = new Set<number>(preassignedIds);
  const queue = [...rootIds, ...reached];
  const expanded = new Set<number>();

  while (queue.length > 0) {
    const parentId = queue.shift() as number;
    if (expanded.has(parentId)) continue;
    expanded.add(parentId);
    for (const childId of childIdsOf(assignments, parentId)) {
      reached.add(childId);
      queue.push(childId);
    }
  }
  return reached;
}

/**
 * Drop links hanging from parents that are not part of the tree, so the
 * backend never sees a CPE uplink through an unassigned Last Mile.
 */
export function pruneUnreachable(
  assignments: ProductAssignment[],
  rootIds: Iterable<number>,
  preassignedIds: Iterable<number> = []
): ProductAssignment[] {
  const roots = new Set(rootIds);
  const reached = reachableLineIds(assignments, roots, preassignedIds);
  return assignments.filter(a => roots.has(a.parentLineId) || reached.has(a.parentLineId));
}

/**
 * Lines that can still be hung under a nested parent: the right category,
 * not already under it, and either unplaced or allowed several parents.
 */
export function nestedCandidates(
  parent: OdooOrderLine,
  lines: OdooOrderLine[],
  assignments: ProductAssignment[],
  reached: Set<number>
): OdooOrderLine[] {
  const current = childIdsOf(assignments, parent.id);
  return lines.filter(
    line =>
      line.id !== parent.id &&
      acceptsNestedChild(parent.product.category, line.product.category) &&
      !current.includes(line.id) &&
      (!reached.has(line.id) || allowsMultipleParents(line.product.category))
  );
}
